import * as React from 'react';
import PaneView from './PaneView';
import RejectContextMenu from './RejectContextMenu';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getStory } from '../redux/actions/active-story';
import { getCachedPlaybackSettings } from '../redux/actions/playback-settings';
import { getCachedPanesState } from '../redux/actions/panes';
import { api, APP_LABEL, SERVICE_ID } from '../constants/app';
import { getVitePublicEnv } from '../runtimeEnv';

import {
    AppWrapper,
    Spinner,
    ToastService,
    Modal,
    getStoryPublishStatus,
    clearStoryPublishStatus,
    HEADER_HEIGHT,
    resetStoryFilter,
    setAccessData,
    pushRoute
} from '@imposium-hub/components';
import PublishWizard from '@imposium-hub/components/dist/esm/components/publish-wizard/PublishWizard';
import {
    setPublishWizard,
    selectExperience,
    clearExperiencesFilters
} from '../redux/actions/experiences';
import { createFreshBatch, deleteBatch, getBatches, setErrorModal } from '../redux/actions/batches';
import { getBatchExport, importBatchFromCsv, renderBatch } from '../redux/actions/batch-jobs';
import { addTag, selectTag } from '../redux/actions/tags';
import { RowErrorModal } from './previewer/RowErrorModal';
import history from '../util/history';

interface IAppProps {
    access: any;
    location: any;
    match: { params: { org_id?: string; story_id?: string } };
    story: any;
    experiencesList: any;
    batchesList: any;
    getStory(id: string): any;
    getCachedPlaybackSettings: () => any;
    getCachedPanesState: () => any;
    setAccessData(data: any): void;
    clearStoryPublishStatus(apiInstance: any): void;
    getStoryPublishStatus(apiInstance: any, sId: string): any;
    setPublishWizard: (e: boolean) => any;
    setErrorModal: (e: boolean, i: any) => any;
    createFreshBatch: (e: string) => any;
    getBatches: () => any;
    getBatchExport: (batchId: string) => any;
    importBatchFromCsv: (
        storyId: string,
        batchId: string,
        csvFile: File,
        accessKey: string,
        compositionId: string,
        addEmbed?: boolean,
        addMedia?: boolean
    ) => any;
    renderBatch: (batchId: string, postRenderActions?: any) => any;
    batchJobs: any;
    deleteBatch: (batchId: string) => any;
    selectExperience: (exp: any) => any;
    clearExperiencesFilters(): void;
    selectTag: (tag: any) => any;
    addTag: (tag: any) => any;
    resetStoryFilter(): any;
    storyFilter: any;
}

interface IAppState {
    isPublish: boolean;
}

class App extends React.PureComponent<IAppProps, IAppState> {
    constructor(props) {
        super(props);

        this.props.getCachedPanesState();
        this.props.getCachedPlaybackSettings();
    }

    public componentDidMount() {
        const {
            location: { search },
            match: {
                params: { org_id: organizationIdParam }
            }
        } = this.props;
        const query = new URLSearchParams(search);
        const organizationIdQueryParam = query.get('organization_id');
        const batchIdParam = query.get('batch_id');

        if (organizationIdQueryParam) {
            if (batchIdParam) {
                pushRoute(
                    `/${organizationIdQueryParam}/${organizationIdParam}?batch_id=${batchIdParam}`
                );
            } else {
                pushRoute(`/${organizationIdQueryParam}/${organizationIdParam}`);
            }
        }
    }

    public onAuthenticated = (activeOrgId, activeStoryId) => {
        this.onStoryIdChange(activeStoryId);
    };

    private onStoryIdChange = (storyId: string): void => {
        const { storyFilter } = this.props;
        if (storyFilter) {
            this.props.resetStoryFilter();
        }
        this.props.clearStoryPublishStatus(api);
        this.props.getStory(storyId);
        this.props.getStoryPublishStatus(api, storyId).catch((e) => ToastService.emit('error', e));
        this.props.selectExperience({});
        this.props.clearExperiencesFilters();
    };

    public getProjectId = (data: any) => {
        for (const key in data.acts) {
            if (data.acts.hasOwnProperty(key)) {
                return data.acts[key].id;
            }
        }
    };

    public render() {
        const {
            match: {
                params: { story_id: storyIdParam, org_id: organizationIdParam }
            },
            location: { search },
            story: { data },
            experiencesList: { publishWizardOpen },
            batchesList: {
                errorModal: { isOpen }
            },
            batchJobs
        } = this.props;
        const batchIdParam = new URLSearchParams(search).get('batch_id');

        const project = { actId: this.getProjectId(data) };

        const fromCrM = data && data.creativeLibraryId;
        const CrMLink =
            fromCrM && data
                ? data.creativeId
                    ? `${getVitePublicEnv('VITE_CRM_BASE')}/library/${
                          data.creativeLibraryId
                      }/creative/${data.creativeId}/versions/`
                    : `${getVitePublicEnv('VITE_CRM_BASE')}/library/${data.creativeLibraryId}`
                : null;
        const hideHeaderUI = fromCrM && data && data.creativeLibraryId ? true : false;

        return (
            <AppWrapper
                appLabel={APP_LABEL}
                environment={getVitePublicEnv('VITE_ENV')}
                baseUrl={getVitePublicEnv('VITE_IMPOSIUM_BASE')}
                growthBookId={getVitePublicEnv('VITE_GROWTH_BOOK_ID')}
                api={api}
                serviceId={SERVICE_ID}
                history={history}
                organizationId={organizationIdParam}
                storyId={storyIdParam}
                CrMLink={CrMLink}
                hideStoryPicker={false}
                hideDocs={hideHeaderUI}
                hideOrgPicker={false}
                setAccessData={this.props.setAccessData}
                allowNoStories={false}
                onAuthenticated={this.onAuthenticated}>
                <PaneView
                    organizationId={organizationIdParam}
                    batchId={batchIdParam}
                />
                {this.props.story.loading && (
                    <div className='story-loader'>
                        <Spinner />
                    </div>
                )}
                <Modal
                    onRequestClose={() => this.props.setPublishWizard(false)}
                    wrapperStyle={{
                        top: HEADER_HEIGHT,
                        left: '0px',
                        width: '100%',
                        height: `calc(100% - ${HEADER_HEIGHT}px)`
                    }}
                    style={{
                        width: '600px',
                        height: '70%',
                        top: '10%',
                        left: 'calc((100% - 600px) / 2)'
                    }}
                    isOpen={publishWizardOpen}>
                    {data && (
                        <PublishWizard
                            fromCrM={fromCrM}
                            creativeManagerBaseUrl={getVitePublicEnv('VITE_CRM_BASE')}
                            api={api}
                            story={data}
                            project={project}
                            onClose={() => this.props.setPublishWizard(false)}
                            handleError={(e) => ToastService.emit('error', e)}
                            handleNotification={(i) => ToastService.emit('info', i)}
                            createFreshBatch={this.props.createFreshBatch}
                            getBatches={this.props.getBatches}
                            getBatchExport={this.props.getBatchExport}
                            importBatchFromCsv={this.props.importBatchFromCsv}
                            renderBatch={this.props.renderBatch}
                            batchJobs={batchJobs}
                            onDeleteBatch={this.props.deleteBatch}
                        />
                    )}
                </Modal>
                {isOpen && (
                    <RowErrorModal
                        setErrorModal={(a, i) => this.props.setErrorModal(a, i)}
                        selectTag={(t) => this.props.selectTag(t)}
                        addTag={(t) => this.props.addTag(t)}
                        batchesList={this.props.batchesList}
                    />
                )}
                <RejectContextMenu />
            </AppWrapper>
        );
    }
}

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators(
        {
            getStory,
            getCachedPlaybackSettings,
            getCachedPanesState,
            setPublishWizard,
            createFreshBatch,
            getBatches,
            getBatchExport,
            importBatchFromCsv,
            getStoryPublishStatus,
            clearStoryPublishStatus,
            renderBatch,
            deleteBatch,
            selectExperience,
            clearExperiencesFilters,
            setErrorModal,
            selectTag,
            addTag,
            setAccessData,
            resetStoryFilter
        },
        dispatch
    );
};

const mapStateToProps = (state) => {
    return {
        access: state.access,
        story: state.story,
        experiencesList: state.experiencesList,
        batchJobs: state.batchJobs,
        batchesList: state.batchesList,
        storyFilter: state.storyFilter.name
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
